/**
 * @module Colour
 * @extends String
 * @description A hex colour value with an opacity method,
 *  you may have to first cast the Colour as a String to use it in some React components
 */
export class Colour extends String {
  /**
   * @param {number} [percentage=0] - The percentage of opacity, with 0 being transparent, and 100 being opaque
   * @returns {string} - An RGBA hex colour value
   * @example
   *  const black = new Colour('#000000');
   *  const grey = black.opacity(50); // #00000080
   */
  opacity(percentage = 0) {
    if (!percentage) {
      return 'transparent';
    }

    const opacityValue = Math.floor((percentage / 100) * 255).toString(16);
    return this.valueOf() + opacityValue;
  }
}

// Colours are from the Design System Colour Palette
// https://github.com/EurostarDigital/design-system/blob/master/workspaces/ds/packages/ds-core/src/base/colourPalette.ts
export const Core = {
  CoreBlue: new Colour('#106DFF'),
  CoreNavy: new Colour('#00286A'),
  CoreCream: new Colour('#FCF5D8'),
  CorePink: new Colour('#FF8CDF'),
  CoreGreen: new Colour('#04CD79'),
  SecondaryPlum: new Colour('#7D0552'),
  SecondaryBlue: new Colour('#126DFF'),
  SecondaryRed: new Colour('#F0323B'),
  SecondaryPink: new Colour('#FF8CDF'),
  SecondaryGreen: new Colour('#086264'),
  SecondaryMint: new Colour('#00CD79'),
  SecondaryYellow: new Colour('#FFC305')
};

export const LyriaColors = {
  Ardoise: new Colour('#4b4b4b'),
  Coquelicot: new Colour('#eb0a28'),
  BleuGlacier: new Colour('#a5d4e5')
};

export const SncfColors = {
  PaleBlueLily: new Colour('#DAEAF3'),
  CoralBlue: new Colour('#B8D9F0'),
  PaleCerulean: new Colour('#A2BBEE')
};

export const SncbColors = {
  MidnightBlue: new Colour('#1B3B6E'),
  VividBlue: new Colour('#0069B4')
};

export const DBColours = {
  firstClass: new Colour('#1B3B6E'),
  secondClass: new Colour('#0069B4')
};

export const NSColours = {
  firstClass: new Colour('#1B3B6E'),
  secondClass: new Colour('#0069B4')
};

export const Greyscale = {
  Black: new Colour('#000000'),
  Charcoal: new Colour('#1C1C1A'),
  CharcoalGrey: new Colour('#55565A'),
  SlateGrey: new Colour('#393938'),
  SteelGrey: new Colour('#646463'),
  SmokeGrey: new Colour('#949492'),
  CloudGrey: new Colour('#C8C8C7'),
  SilverGrey: new Colour('#E3E3E3'),
  MistGrey: new Colour('#F2F2F2'),
  White: new Colour('#FFFFFF'),
  Eggshell: new Colour('#FBFBFB')
};

export const Shades = {
  BlueShade20: new Colour('#0D57CC'),
  BlueShade40: new Colour('#0A4199'),
  BlueShade60: new Colour('#062C66'),
  CreamShade40: new Colour('#FDFDF0'),
  GreenTint90: new Colour('#E6EFEF'),
  NavyShade20: new Colour('#002055'),
  NavyTint90: new Colour('#E5E9F0'),
  PlumShade40: new Colour('#4B0331'),
  PlumShade60: new Colour('#320221'),
  PlumTint30: new Colour('#A45086'),
  PlumTint90: new Colour('#F2E6EE'),
  TintBlue10: new Colour('#E7F0FF'),
  TintBlue20: new Colour('#CFE2FF'),
  TintBlue40: new Colour('#9FC5FF'),
  TintMint40: new Colour('#99EBC9'),
  TintNavy90: new Colour('#E5E9F0'),
  YellowShade20: new Colour('#CC9C04'),
  YellowShade40: new Colour('#997503')
};

export const Opacity = {
  Transparent: new Colour('transparent'),
  WhiteOpacity20: new Colour('rgb(255, 255, 255)').opacity(20),
  WhiteOpacity40: new Colour('rgb(255, 255, 255)').opacity(40),
  BlueOpacity10: new Colour('rgb(16, 109, 255)').opacity(10),
  CreamOpacity20: new Colour('rgb(252, 245, 216)').opacity(20),
  CreamOpacity40: new Colour('rgb(252, 245, 216)').opacity(40),
  BlackOpacity10: new Colour('rgb(0, 0, 0)').opacity(10),
  NavyOpacity20: new Colour('rgb(0, 40, 106)').opacity(20),
  NavyOpacity40: new Colour('rgb(0, 40, 106)').opacity(40)
};

export const Messaging = {
  WarningOrange: new Colour('#FC9B46'),
  WarningOrangeLight: new Colour('#FFE999'),
  InfoBlue: new Colour('#106DFF'),
  InfoBlueLight: new Colour('#D1EFFF'),
  SuccessGreen: new Colour('#278811'),
  SuccessGreenLight: new Colour('#D1F8C8'),
  ErrorRed: new Colour('#D73E2C'),
  ErrorRedLight: new Colour('#FBE0DD')
};
